import { BRAND_NAME, CONTACT_EMAIL } from "../../constants/brand";
import { useState, useEffect } from 'react';
import { Grid, Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import agent from '../../service/api';
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import styles from '../../Styles/ComponentStyles/SetNewPassword/SetNewPassword.module.scss';
import newRBlogo from '../../assets/Images/mar-lockup.svg';

// ── SVG icons ──────────────────────────────────────────────

const ShieldCheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const LockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const EyeIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const EyeOffIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

// ─────────────────────────────────────────────────────────

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$.!%*#?&])[A-Za-z\d@$.!%*#?&]{8,}$/;

interface AgentDetails {
    first_name: string;
    last_name: string;
    email: string;
    user_id: number;
}

const SetNewPassword = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const setupToken = new URLSearchParams(location.search).get('setup_token') ?? '';

    const [agentDetails, setAgentDetails] = useState<AgentDetails | null>(null);
    const [isFetchingDetails, setIsFetchingDetails] = useState(true);

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ── Derived validation states ──
    const passwordValid = PASSWORD_REGEX.test(password);
    const passwordsMatch = password === confirmPassword && confirmPassword !== '';
    const canSubmit = passwordValid && passwordsMatch && !isSubmitting;

    // ── Fetch agent details on mount using setup_token ──
    useEffect(() => {
        if (!setupToken) return;

        const fetchAgentDetails = async () => {
            try {
                setIsFetchingDetails(true);
                const response = await agent.getAgentDetailsWithTokenAPI.get(setupToken);

                if (response?.isAxiosError || response?.status === false) {
                    showSnackbar(
                        response?.data?.error?.message ||
                        response?.error?.message ||
                        'Invalid or expired setup link.',
                        'error'
                    );
                    navigate('/');
                    return;
                }

                if (response?.status && response?.data) {
                    setAgentDetails({
                        first_name: response.data.first_name ?? '',
                        last_name: response.data.last_name ?? '',
                        email: response.data.email ?? '',
                        user_id: response.data.user_id,
                    });
                }
            } catch {
                showSnackbar('Failed to validate setup link.', 'error');
                navigate('/');
            } finally {
                setIsFetchingDetails(false);
            }
        };

        fetchAgentDetails();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Submit: set password ──
    const handleSubmit = async () => {
        if (!canSubmit) return;

        try {
            setIsSubmitting(true);
            const response = await agent.setNewPasswordAPI.post({
                setup_token: setupToken,
                password,
                confirm_password: confirmPassword,
            });

            if (response?.isAxiosError || response?.status === false) {
                showSnackbar(
                    response?.data?.error?.message ||
                    response?.error?.message ||
                    'Failed to set password.',
                    'error'
                );
                return;
            }

            if (response?.status) {
                showSnackbar(
                    response?.data?.message || 'Password has been set successfully.',
                    'success'
                );
                navigate('/login');
            }
        } catch {
            showSnackbar('Failed to set password.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const fullName = agentDetails
        ? `${agentDetails.first_name} ${agentDetails.last_name}`.trim()
        : '';

    if (isFetchingDetails) {
        return (
            <Grid container justifyContent="center" alignItems="center" className={styles.pageWrapper}>
                <p className={styles.helpText}>Validating your setup link...</p>
            </Grid>
        );
    }

    return (
        <Grid
            container
            direction="column"
            alignItems="center"
            className={styles.pageWrapper}
        >
            {/* ── Logo header ── */}
            <Grid item xs={12} sm={10} md={5} lg={4} xl={4} className={styles.header}>
                <img
                    src={newRBlogo}
                    alt={BRAND_NAME}
                    className={styles.logoImg}
                />
            </Grid>

            {/* ── Card ── */}
            <Grid item xs={12} sm={10} md={5} lg={4} xl={4} style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 16px' }}>
                <Box className={styles.card}>

                    {/* ── Blue hero banner ── */}
                    <Box className={styles.heroBanner}>
                        <Box className={styles.shieldIconWrapper}>
                            <ShieldCheckIcon />
                        </Box>
                        <p className={styles.bannerTitle}>Set Up Your Password</p>
                        <p className={styles.bannerSubtitle}>
                            Congratulations! Your account has been approved. Please create a secure password
                            to complete your registration.
                        </p>
                    </Box>

                    {/* ── Form body ── */}
                    <Box className={styles.formBody}>

                        {/* Approval notice */}
                        <Box className={styles.approvalNotice}>
                            <Box className={styles.approvalIconWrapper}>
                                <CheckCircleIcon />
                            </Box>
                            <Box className={styles.approvalTextGroup}>
                                <p className={styles.approvalTitle}>
                                    {fullName ? `Hey ${fullName}, Your Account Has Been Approved!` : 'Your Account Has Been Approved!'}
                                </p>
                                <p className={styles.approvalDesc}>
                                    Complete this final step to activate your portal access and start using all
                                    available features.
                                </p>
                            </Box>
                        </Box>

                        {/* Create Password */}
                        <Box className={styles.fieldGroup}>
                            <label htmlFor="create-password" className={styles.fieldLabel}>
                                Create Password
                            </label>
                            <Box className={`${styles.inputWrapper} ${password && !passwordValid ? styles.inputError : ''}`}>
                                <Box className={styles.inputIcon}>
                                    <LockIcon />
                                </Box>
                                <input
                                    id="create-password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={styles.input}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className={styles.eyeButton}
                                    onClick={() => setShowPassword((p) => !p)}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                                </button>
                            </Box>
                            {password && !passwordValid && (
                                <p className={styles.fieldError}>
                                    Min 8 characters with uppercase, lowercase, number and special character (@$.!%*#?&).
                                </p>
                            )}
                        </Box>

                        {/* Confirm Password */}
                        <Box className={styles.fieldGroup}>
                            <label htmlFor="confirm-password" className={styles.fieldLabel}>
                                Confirm Password
                            </label>
                            <Box className={`${styles.inputWrapper} ${confirmPassword && !passwordsMatch ? styles.inputError : ''}`}>
                                <Box className={styles.inputIcon}>
                                    <LockIcon />
                                </Box>
                                <input
                                    id="confirm-password"
                                    type={showConfirm ? 'text' : 'password'}
                                    placeholder="Re-enter your password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={styles.input}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className={styles.eyeButton}
                                    onClick={() => setShowConfirm((p) => !p)}
                                    tabIndex={-1}
                                >
                                    {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                                </button>
                            </Box>
                            {confirmPassword && !passwordsMatch && (
                                <p className={styles.fieldError}>Passwords do not match.</p>
                            )}
                        </Box>

                        {/* Submit */}
                        <button
                            type="button"
                            className={styles.submitBtn}
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                        >
                            <LockIcon />
                            {isSubmitting ? 'Activating...' : 'Set Password & Activate Account'}
                        </button>
                    </Box>
                </Box>
            </Grid>

            {/* ── Footer help text ── */}
            <Grid item xs={12} sm={10} md={5} lg={4} xl={4}>
                <p className={styles.helpText}>
                    Need help? Contact our support team at{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </p>
            </Grid>
        </Grid>
    );
};

export default SetNewPassword;